import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import ReactMarkdown from "react-markdown";
import { IoMdChatboxes } from "react-icons/io";

// Clean citations from content
const cleanCitations = (text) => {
  if (!text) return text;
  // Also handle variations with different brackets or formats
  return text
    .replace(/【\d+:\d+†[^】]+】/g, '') // Match any text after † until closing bracket
    .replace(/\[\d+:\d+†[^\]]+\]/g, '') // Match square brackets format
    .replace(/\s*【\d+:\d+†[^】]+】\s*/g, '') // Match with surrounding whitespace
    .replace(/\n\s*\n\s*\n/g, '\n\n') // Clean up excessive line breaks
    .trim();
};

// Markdown renderer component with proper heading numbering
const MarkdownRenderer = ({ content }) => {
  // Pre-parse content to assign numbers before rendering
  const numberedContent = useMemo(() => {
    let h2Count = 0;
    let h3Count = 0;
    const lines = content.split('\n');
    const processed = lines.map((line, index) => {
      // Check if line is H1 - reset counters, don't number
      if (line.match(/^#\s+/)) {
        h2Count = 0;
        h3Count = 0;
        return line;
      }
      // Check if line is H2 - add number
      if (line.match(/^##\s+/)) {
        h2Count++;
        h3Count = 0;
        return line.replace(/^##\s+/, `## ${h2Count}. `);
      }
      // Check if line is H3 - add hierarchical number
      if (line.match(/^###\s+/)) {
        h3Count++;
        const prefix = h2Count > 0 ? `${h2Count}.${h3Count}. ` : `${h3Count}. `;
        return line.replace(/^###\s+/, `### ${prefix}`);
      }
      return line;
    });
    return processed.join('\n');
  }, [content]);

  // Create components with stable references
  const components = useMemo(() => ({
    h1: ({ node, children, ...props }) => {
      // H1 headings - no numbers
      return (
        <h1 className="text-base font-bold mt-0 mb-3 text-gray-900" {...props}>
          {children}
        </h1>
      );
    },
    h2: ({ node, children, ...props }) => {
      // Numbers are already in the content from pre-processing
      return (
        <h2 className="text-sm font-semibold mt-4 mb-2 text-gray-900 first:mt-0" {...props}>
          {children}
        </h2>
      );
    },
    h3: ({ node, children, ...props }) => {
      // Numbers are already in the content from pre-processing
      return (
        <h3 className="text-sm font-semibold mt-3 mb-2 text-gray-900" {...props}>
          {children}
        </h3>
      );
    },
    ul: ({ node, ...props }) => (
      <ul className="my-2 ml-4 list-disc text-gray-800" {...props} />
    ),
    ol: ({ node, ...props }) => (
      <ol className="my-2 ml-4 list-decimal text-gray-800" {...props} />
    ),
  }), []);

  return (
    <div className="text-sm leading-relaxed prose prose-sm max-w-none prose-headings:text-gray-900 prose-headings:font-semibold prose-headings:mt-4 prose-headings:mb-2 prose-h1:text-base prose-h1:font-bold prose-h1:mt-0 prose-h1:mb-3 prose-h2:text-sm prose-h2:font-semibold prose-h2:mt-4 prose-h2:mb-2 prose-h2:first:mt-0 prose-h3:text-sm prose-h3:font-semibold prose-h3:mt-3 prose-h3:mb-2 prose-p:text-gray-800 prose-p:my-2 prose-p:leading-relaxed prose-ul:text-gray-800 prose-ul:my-2 prose-ul:ml-4 prose-ul:list-disc prose-ol:text-gray-800 prose-ol:my-2 prose-ol:ml-4 prose-ol:list-decimal prose-li:text-gray-800 prose-li:my-1 prose-li:leading-relaxed prose-strong:text-gray-900 prose-strong:font-semibold prose-code:text-gray-800 prose-code:bg-gray-100 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:text-xs prose-pre:bg-gray-100 prose-pre:my-2 prose-pre:rounded prose-pre:p-3 prose-blockquote:border-l-4 prose-blockquote:border-gray-300 prose-blockquote:pl-4 prose-blockquote:italic prose-a:text-[#3a599c] prose-a:no-underline hover:prose-a:underline prose-a:font-medium prose-hr:my-4 prose-hr:border-gray-200">
      <ReactMarkdown components={components}>
        {numberedContent}
      </ReactMarkdown>
    </div>
  );
};

export default function ChatBot() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [threadId, setThreadId] = useState(null);
  const scrollRef = useRef();
  
  // Draggable position state
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [hasMoved, setHasMoved] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const buttonRef = useRef();

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading, open]);

  // Removed dummy welcome message initialization

  // Dragging functionality
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isDragging) {
        setHasMoved(true);
        const newX = e.clientX - dragOffset.x;
        const newY = e.clientY - dragOffset.y;
        
        // Responsive button size for boundary calculation
        const buttonSize = window.innerWidth < 640 ? 56 : window.innerWidth < 768 ? 64 : 80;
        const maxX = window.innerWidth - buttonSize;
        const maxY = window.innerHeight - buttonSize;
        
        setPosition({
          x: Math.max(0, Math.min(newX, maxX)),
          y: Math.max(0, Math.min(newY, maxY)),
        });
      }
    };

    const handleTouchMove = (e) => {
      if (isDragging) {
        e.preventDefault(); // Prevent scrolling while dragging
        const touch = e.touches[0];
        setHasMoved(true);
        const newX = touch.clientX - dragOffset.x;
        const newY = touch.clientY - dragOffset.y;
        
        // Responsive button size for boundary calculation
        const buttonSize = window.innerWidth < 640 ? 56 : window.innerWidth < 768 ? 64 : 80;
        const maxX = window.innerWidth - buttonSize;
        const maxY = window.innerHeight - buttonSize;
        
        setPosition({
          x: Math.max(0, Math.min(newX, maxX)),
          y: Math.max(0, Math.min(newY, maxY)),
        });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      setTimeout(() => setHasMoved(false), 100);
    };


    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchmove', handleTouchMove);
    };
  }, [isDragging, dragOffset]);

  const handleMouseDown = (e) => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
      setIsDragging(true);
    }
  };

  const handleTouchStart = (e) => {
    if (buttonRef.current) {
      const touch = e.touches[0];
      const rect = buttonRef.current.getBoundingClientRect();
      setDragOffset({
        x: touch.clientX - rect.left,
        y: touch.clientY - rect.top,
      });
      setIsDragging(true);
      // Store initial touch position to detect if it's a tap vs drag
      buttonRef.current.dataset.touchStartX = touch.clientX;
      buttonRef.current.dataset.touchStartY = touch.clientY;
    }
  };

  const handleTouchEnd = (e) => {
    if (buttonRef.current && !hasMoved) {
      // If no movement occurred, treat it as a tap and open chat
      const touchStartX = parseFloat(buttonRef.current.dataset.touchStartX || '0');
      const touchStartY = parseFloat(buttonRef.current.dataset.touchStartY || '0');
      const touchEnd = e.changedTouches[0];
      
      // Check if touch moved significantly (more than 5px)
      const moved = Math.abs(touchEnd.clientX - touchStartX) > 5 || 
                    Math.abs(touchEnd.clientY - touchStartY) > 5;
      
      if (!moved) {
        setOpen(true);
      }
    }
    setIsDragging(false);
    setTimeout(() => setHasMoved(false), 100);
  };

  // Play sound function
  const playSound = (type) => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      if (type === 'send') {
        oscillator.frequency.setValueAtTime(400, audioContext.currentTime);
        oscillator.frequency.exponentialRampToValueAtTime(800, audioContext.currentTime + 0.1);
        gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.1);
        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.1);
      } else if (type === 'receive') {
        oscillator.frequency.setValueAtTime(700, audioContext.currentTime);
        oscillator.frequency.setValueAtTime(500, audioContext.currentTime + 0.05);
        gainNode.gain.setValueAtTime(0.2, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.15);
        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.15);
      }
    } catch (error) {
      console.log('Audio play failed:', error);
    }
  };

  async function sendMessage(prompt) {
    const composed = typeof prompt === "string" ? prompt : input;
    if (!composed.trim() || loading) return;

    const text = composed.trim();
    setMessages((m) => [...m, { role: "user", content: text }]);
    setInput("");
    setLoading(true);
    
    playSound('send');

    try {
      const res = await fetch("/api/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: text, threadId: threadId }),
      });

      const data = await res.json();

      if (res.ok) {
        setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
        // Store threadId for conversation continuity
        if (data.threadId) {
          setThreadId(data.threadId);
        }
        playSound('receive');
      } else {
        setMessages((m) => [
          ...m,
          {
            role: "assistant",
            content: data.reply || "Sorry, I encountered an error. Please try again.",
          },
        ]);
        playSound('receive');
      }
    } catch (e) {
      console.error("Chat error:", e);
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content: "Network error. Please check your connection and try again.",
        },
      ]);
      playSound('receive');
    } finally {
      setLoading(false);
    }
  }

  function handleQuickAction(text) {
    // Immediately send the selected quick action as a user message
    sendMessage(text);
  }

  function clearChat() {
    setMessages([]);
    setThreadId(null); // Reset thread for new conversation
  }

  return (
    <div 
      className="fixed z-[9999]"
      style={{
        left: position.x ? `${position.x}px` : 'auto',
        top: position.y ? `${position.y}px` : 'auto',
        right: position.x ? 'auto' : '1rem',
        bottom: position.y ? 'auto' : '1rem',
        cursor: isDragging ? 'grabbing' : 'grab'
      }}
    >
      {/* Floating button */}
      {/* Floating button with animated video */}
{!open && (
  <button
    ref={buttonRef}
    onMouseDown={handleMouseDown}
    onTouchStart={handleTouchStart}
    onTouchEnd={handleTouchEnd}
    onClick={(e) => {
      if (!hasMoved) {
        setOpen(true);
      }
    }}
    aria-label="Open Hyrelancer AI Assistant"
    className="w-14 h-14 sm:w-15 sm:h-15 md:w-17 md:h-17 xl:w-18 xl:h-18 bg-gradient-to-br from-[#3a599c] to-[#2f477c] rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center overflow-hidden fixed hover:scale-105 active:scale-95 border-2 border-white/20"
    style={{
      touchAction: 'none',
      right: position.x ? 'auto' : '1rem',
      bottom: position.y ? 'auto' : '1rem',
      left: position.x ? `${position.x}px` : 'auto',
      top: position.y ? `${position.y}px` : 'auto',
      zIndex: 9999,
      cursor: isDragging ? 'grabbing' : 'pointer',
      WebkitTouchCallout: 'none',
      WebkitUserSelect: 'none',
      userSelect: 'none',
      boxShadow: '0 10px 25px -5px rgba(58, 89, 156, 0.4), 0 10px 10px -5px rgba(58, 89, 156, 0.2)',
    }}
  >
    <IoMdChatboxes 
      className="w-7 h-7 sm:w-8 sm:h-8 md:w-9 md:h-9 text-white"
      style={{ pointerEvents: 'none' }}
    />
  </button>
)}


      {/* Chat window */}
      {open && (
        <div className="fixed inset-x-0 bottom-0 sm:inset-x-auto sm:right-4 sm:bottom-4 w-full sm:w-96 h-[100dvh] sm:h-[550px] bg-gray-50 border-0 sm:border sm:bg-white border-gray-200 rounded-none sm:rounded-2xl shadow-2xl flex flex-col overflow-hidden animate-slideUp">
          {/* Header */}
          <div className="bg-[#3a599c] text-white px-4 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center overflow-hidden">
                    <img
                      src="/images/pattern-blue.png"
                      alt="Hyrelancer"
                      className="w-full h-full object-contain"
                      onError={(e) => {
                        // Fallback to logo-white if hyrelancerWhite doesn't exist
                        e.target.src = "/images/logo-white.png";
                      }}
                    />
                  </div>
                  {/* <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-400 rounded-full border-2 border-white"></div> */}
                </div>
                <div>
                  <div className="font-semibold">Hyrelia</div>
                  <div className="text-xs text-white/80 flex items-center gap-1">
                    <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                    Online
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={clearChat}
                  className="text-white/80 hover:text-white p-2 rounded-none hover:bg-white/10 transition-all"
                  title="Clear chat"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="1 4 1 10 7 10" />
                    <polyline points="23 20 23 14 17 14" />
                    <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15" />
                  </svg>
                </button>
                <button
                  onClick={() => setOpen(false)}
                  className="text-white/80 hover:text-white p-2 rounded-none hover:bg-white/10 transition-all"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
            </div>
          </div>

          {/* Messages area */}
          <div
            ref={scrollRef}
            className="flex-1 p-4 overflow-y-auto space-y-4 bg-gray-50"
          >
            {messages.length === 0 && !loading && (
              <div className="bg-white rounded-none shadow-sm p-4 border border-gray-100">
                <div className="flex items-start gap-3">
                  <div>
                    <div className="font-medium text-gray-900 mb-1">Hi! I'm your Hyrelancer assistant. I can help you with:</div>
                    <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
                      <li>Account registration and user type selection</li>
                      <li>How can a freelancer complete their profile setup?</li>
                      <li>Finding and applying for jobs</li>
                      <li>Searching for and contacting freelancers</li>
                    </ul>
                    <div className="text-sm text-gray-600 mt-3">What would you like to know?</div>
                  </div>
                </div>
              </div>
            )}
            {messages.map((m, i) => {
              return (
                <div
                  key={i}
                  className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[85%] px-4 py-3 rounded-none shadow-sm ${
                      m.role === "user"
                        ? "bg-[#3a599c] text-white"
                        : "bg-white text-gray-800"
                    }`}
                  >
                    {m.role === "user" ? (
                      <div className="text-sm leading-relaxed whitespace-pre-line">
                        {m.content}
                      </div>
                    ) : (
                      <MarkdownRenderer content={cleanCitations(m.content)} />
                    )}
                  </div>
                </div>
              );
            })}

            {loading && (
              <div className="flex justify-start">
                <div className="px-4 py-3 rounded-none bg-white shadow-sm">
                  <div className="flex items-center gap-2 text-gray-500">
                    <div className="flex gap-1">
                      <div className="w-2 h-2 bg-[#3a599c] rounded-full animate-bounce"></div>
                      <div className="w-2 h-2 bg-[#3a599c] rounded-full animate-bounce" style={{ animationDelay: "0.1s" }}></div>
                      <div className="w-2 h-2 bg-[#3a599c] rounded-full animate-bounce" style={{ animationDelay: "0.2s" }}></div>
                    </div>
                    <span className="text-xs">Thinking...</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Input area */}
          <div className="px-4 py-3 bg-white border-t border-gray-200">
            <div className="flex gap-2 mb-2">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && sendMessage()}
                placeholder="Ask me anything..."
                disabled={loading}
                className="flex-1 px-4 py-3 rounded-none border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#3a599c] focus:border-[#3a599c] disabled:bg-gray-50"
              />
              <button
                onClick={() => sendMessage()}
                disabled={loading || !input.trim()}
                className="px-5 py-3 rounded-none bg-[#3a599c] text-white font-medium hover:bg-[#2f477c] disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="22" y1="2" x2="11" y2="13" />
                  <polygon points="22 2 15 22 11 13 2 9 22 2" />
                </svg>
              </button>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                type="button"
                onClick={() => handleQuickAction("Find jobs")}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-none bg-gray-100 text-gray-700 text-xs font-medium hover:bg-gray-200"
                aria-label="Find Jobs"
              >
                <span className="text-[#3a599c]">🔎</span>
                Find Jobs
              </button>
              <button
                type="button"
                onClick={() => handleQuickAction("Post a job")}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-none bg-gray-100 text-gray-700 text-xs font-medium hover:bg-gray-200"
                aria-label="Post a Job"
              >
                <span className="text-yellow-600">🏷️</span>
                Post a Job
              </button>
              <button
                type="button"
                onClick={() => handleQuickAction("Find freelancers")}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-none bg-gray-100 text-gray-700 text-xs font-medium hover:bg-gray-200"
                aria-label="Find Freelancers"
              >
                <span className="text-[#3a599c]">🔎</span>
                Find Freelancers
              </button>
              {/* <button
                type="button"
                onClick={() => handleQuickAction("Pricing")}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-full bg-gray-100 text-gray-700 text-xs font-medium hover:bg-gray-200"
                aria-label="Pricing"
              >
                <span className="text-amber-600">💰</span>
                Pricing
              </button> */}
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes bounce-slow {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-5px);
          }
        }

        @keyframes pulse-hover {
          0%, 100% {
            box-shadow: 0 20px 25px -5px rgba(58, 89, 156, 0.3), 0 10px 10px -5px rgba(58, 89, 156, 0.2);
          }
          50% {
            box-shadow: 0 25px 30px -5px rgba(58, 89, 156, 0.4), 0 15px 15px -5px rgba(58, 89, 156, 0.3);
          }
        }

        .animate-slideUp {
          animation: slideUp 0.3s ease-out;
        }

        .animate-bounce-slow {
          animation: bounce-slow 2s ease-in-out infinite;
        }

        .animate-pulse-hover {
          animation: pulse-hover 2s ease-in-out infinite;
        }
      `}</style>
      <style jsx>{`
  @keyframes float {
    0% { transform: translateY(0px); box-shadow: 0 10px 25px rgba(58, 89, 156, 0.3); }
    50% { transform: translateY(-8px); box-shadow: 0 20px 30px rgba(58, 89, 156, 0.4); }
    100% { transform: translateY(0px); box-shadow: 0 10px 25px rgba(58, 89, 156, 0.3); }
  }

  @keyframes glow {
    0%, 100% { filter: drop-shadow(0 0 5px rgba(58, 89, 156, 0.6)); }
    50% { filter: drop-shadow(0 0 15px rgba(58, 89, 156, 0.9)); }
  }

  .animate-float {
    animation: float 3s ease-in-out infinite, glow 2s ease-in-out infinite;
  }
`}</style>

    </div>
  );
} 